import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/react';
import { aigleInputStyles } from '@styles/inputStyleGuide';
import { aigleColor } from '@styles/colorStyleGuide';

/**
 * Select Option 타입
 */
export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

/**
 * AigleSelect Props
 */
export interface AigleSelectProps {
  /** 옵션 리스트 */
  options: SelectOption[];
  /** 선택된 값 */
  value?: string | number;
  /** 기본 선택 값 */
  defaultValue?: string | number;
  /** 변경 이벤트 핸들러 */
  onChange?: (value: string | number, option: SelectOption) => void;
  /** Placeholder */
  placeholder?: string;
  /** Label 텍스트 */
  label?: string;
  /** Helper/Error 메시지 */
  helperText?: string;
  /** 에러 상태 여부 */
  error?: boolean;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 전체 너비 사용 여부 */
  fullWidth?: boolean;
  /** 추가 스타일 */
  customStyle?: SerializedStyles;
  /** 컨테이너 추가 스타일 */
  containerStyle?: SerializedStyles;
}

// Styled Components
const SelectContainer = styled.div<{ $fullWidth: boolean; $containerStyle?: SerializedStyles }>`
  ${aigleInputStyles.container}
  ${({ $fullWidth }) => $fullWidth ? 'width: 100%;' : 'width: auto;'}
  ${({ $containerStyle }) => $containerStyle}
  position: relative;
`;

const SelectLabel = styled.label`
  ${aigleInputStyles.label}
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SelectButton = styled.button<{
  $error: boolean;
  $disabled: boolean;
  $isOpen: boolean;
  $customStyle?: SerializedStyles;
}>`
  ${aigleInputStyles.select.base}
  width: 100%;
  text-align: left;
  
  ${({ $error }) => $error && css`
    border-color: ${aigleColor.semantic.error};
    
    &:focus {
      border-color: ${aigleColor.semantic.error};
    }
  `}
  
  ${({ $disabled }) => $disabled && css`
    ${aigleInputStyles.state.disabled}
  `}
  
  ${({ $isOpen }) => $isOpen && css`
    border-color: ${aigleColor.brand.primary};
  `}
  
  ${({ $customStyle }) => $customStyle}
  border-radius: 8px;
`;

const SelectIcon = styled.div<{ $isOpen: boolean }>`
  ${aigleInputStyles.select.icon}
  transition: transform 0.2s ease-in-out;
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  top: 16px;
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  ${aigleInputStyles.select.dropdown}
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
`;

const Option = styled.div<{ $selected: boolean; $disabled: boolean }>`
  ${aigleInputStyles.select.option}
  
  ${({ $selected }) => $selected && css`
    background-color: ${aigleColor.alpha.primary.alpha10};
    color: ${aigleColor.brand.primary};
  `}
  
  ${({ $disabled }) => $disabled && css`
    color: ${aigleColor.text.disabled};
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const HelperText = styled.span<{ $error: boolean }>`
  ${aigleInputStyles.helperText.base}
  ${({ $error }) => $error ? aigleInputStyles.helperText.error : aigleInputStyles.helperText.default}
`;

// 기본 드롭다운 아이콘
const DefaultDropdownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * AigleSelect Component
 * 
 * 드롭다운 선택 컴포넌트
 * 
 * @example
 * // Basic Select
 * <AigleSelect 
 *   options={[
 *     { value: '1', label: 'Option 1' },
 *     { value: '2', label: 'Option 2' }
 *   ]}
 * />
 * 
 * @example
 * // Select with Label and Handler
 * <AigleSelect 
 *   label="국가 선택"
 *   options={countries}
 *   value={selectedCountry}
 *   onChange={(value) => setSelectedCountry(value)}
 * />
 */
export const AigleSelect = React.memo<AigleSelectProps>(
  ({
    options,
    value: controlledValue,
    defaultValue,
    onChange,
    placeholder = 'Options',
    label,
    helperText,
    error = false,
    disabled = false,
    fullWidth = true,
    customStyle,
    containerStyle,
  }) => {
    const [internalValue, setInternalValue] = useState<string | number | undefined>(defaultValue);
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);
    
    // 제어 컴포넌트 vs 비제어 컴포넌트 처리
    const isControlled = controlledValue !== undefined;
    const currentValue = isControlled ? controlledValue : internalValue;
    
    // 선택된 옵션 찾기
    const selectedOption = options.find(opt => opt.value === currentValue);
    const displayText = selectedOption ? selectedOption.label : placeholder;
    
    // 외부 클릭 감지
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      
      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);
    
    const handleToggle = () => {
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    };
    
    const handleSelect = (option: SelectOption) => {
      if (option.disabled) return;
      
      if (isControlled) {
        onChange?.(option.value, option);
      } else {
        setInternalValue(option.value);
        onChange?.(option.value, option);
      }
      
      setIsOpen(false);
    };
    
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (disabled) return;
      
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsOpen(!isOpen);
      } else if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    
    return (
      <SelectContainer ref={selectRef} $fullWidth={fullWidth} $containerStyle={containerStyle}>
        {label && <SelectLabel>{label}</SelectLabel>}
        
        <SelectWrapper>
          <SelectButton
            type="button"
            $error={error}
            $disabled={disabled}
            $isOpen={isOpen}
            $customStyle={customStyle}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            {displayText}
          </SelectButton>
          
          <SelectIcon $isOpen={isOpen}>
            <DefaultDropdownIcon />
          </SelectIcon>
          
          <Dropdown $isOpen={isOpen} role="listbox">
            {options.map((option) => (
              <Option
                key={option.value}
                $selected={option.value === currentValue}
                $disabled={!!option.disabled}
                onClick={() => handleSelect(option)}
                role="option"
                aria-selected={option.value === currentValue}
              >
                {option.label}
              </Option>
            ))}
          </Dropdown>
        </SelectWrapper>
        
        {helperText && <HelperText $error={error}>{helperText}</HelperText>}
      </SelectContainer>
    );
  }
);

AigleSelect.displayName = 'AigleSelect';

export default AigleSelect;

